/* This example requires Tailwind CSS v2.0+ */
import { Fragment } from 'react';
import {
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  LinkIcon,
  LocationMarkerIcon,
} from '@heroicons/react/solid';
import { Link } from 'react-router-dom';
import { formatTitle } from '../utils/formatTitle';
import { nativeApply } from '../utils/applyConfig';
import { feature } from '../utils/features';
import ModernCard from './ModernCard';

const DAY_MS = 24 * 60 * 60 * 1000;
const isOpen = (postExpiry) => !postExpiry || Date.parse(`${postExpiry}T00:00:00Z`) + DAY_MS >= Date.now();

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Card({
  btnId,
  title,
  companyName,
  categories,
  city,
  salary,
  postedOn,
  externalUrl,
  source,
  postExpiry,
}) {
  if (feature('modernCards')) {
    return <ModernCard {...{ btnId, title, companyName, categories, city, salary, postedOn, externalUrl, source, postExpiry }} />;
  }
  // Our own open postings take applications right here (native apply): marked "Direct apply".
  const direct = !externalUrl && isOpen(postExpiry) && Boolean(nativeApply());
  // External jobs (CanadaJL GROWTH-005) open the employer's own page and say where they're from.
  const Wrap = externalUrl
    ? ({ children }) => (
        <a href={externalUrl} target="_blank" rel="noopener noreferrer nofollow" aria-label={`${formatTitle(title)} (opens the employer's site)`}>
          {children}
        </a>
      )
    : ({ children }) => <Link to={`/jobs/${btnId}`}>{children}</Link>;
  return (
    <>
      <Wrap>
        <div className="flex flex-wrap -m-4 ">
          <div className="w-full mb-4 ">
            <div className="border border-gray-200 p-4 rounded-lg bg-gray-100 mb-2 mx-4">
              <div className="lg:flex lg:items-center lg:justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold leading-7 text-gray-900  sm:truncate">
                    {formatTitle(title)}
                  </h2>
                  {direct && (
                    <span className="mt-1 inline-block rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-white">Direct apply</span>
                  )}
                  <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                    {categories && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <BriefcaseIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      {categories}
                    </div>
                    )}
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <LocationMarkerIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      {city}
                    </div>
                    {salary && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <CurrencyDollarIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      {salary}
                    </div>
                    )}
                    {postedOn && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <CalendarIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      Posted on {postedOn}
                    </div>
                    )}
                    {source && (
                      <div className="mt-2 flex items-center text-sm text-gray-500">via {source}</div>
                    )}
                  </div>
                </div>
                <div className="mt-5 flex lg:mt-0 lg:ml-4">
                  <span className="hidden sm:block ml-3">
                    <span
                      className="inline-flex items-center px-4 py-2 border border-primary ease-in duration-100 hover:bg-primary-light rounded-md shadow-sm text-sm font-medium text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <LinkIcon
                        className="-ml-1 mr-2 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      {externalUrl ? 'View on employer site' : 'View'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Wrap>
    </>
  );
}
